import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Pagination, Table, Tabs } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import {
  GET_BONUS_HISTORY,
  GET_CLIENT_PAYMENTS,
  GET_CLIENT_DEPOSIT_TRANSFERS,
  IGetBonusHistoryRequest,
  IGetBonusHistoryResponse,
  IGetClientDepositTransfersRequest,
  IGetClientDepositTransfersResponse,
  IGetClientPaymentsRequest,
  IGetClientPaymentsResponse,
} from 'schemas';
import { SettingsContext } from 'settings';

import { IProfileProps } from './interfaces';
import s from './Profile.module.scss';
import {
  getColumnsPayments,
  getTabs,
  getColumnsBonus,
  getColumnsTransfers,
} from './utils';

export const Profile: FC<IProfileProps> = ({ clientUUID }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [tab, setTabs] = useState<'payments' | 'bonus' | 'transfers'>(
    'payments'
  );

  const TABS = useMemo(() => getTabs(t), [t]);

  const [pagesInfo, setPagesInfo] = useState({ currentPage: 1, lastPage: 1 });

  const [loadPayments, { data: payments, loading }] = useLazyQuery<
    IGetClientPaymentsResponse,
    IGetClientPaymentsRequest
  >(GET_CLIENT_PAYMENTS, {
    variables: { uuid: clientUUID, first: 25 },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const [loadBonus, { data: bonus, loading: loadingBonus }] = useLazyQuery<
    IGetBonusHistoryResponse,
    IGetBonusHistoryRequest
  >(GET_BONUS_HISTORY, {
    variables: { uuid: clientUUID, first: 25 },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const [loadTransfers, { data: transfer, loading: loadingTransfers }] =
    useLazyQuery<
      IGetClientDepositTransfersResponse,
      IGetClientDepositTransfersRequest
    >(GET_CLIENT_DEPOSIT_TRANSFERS, {
      variables: { uuid: clientUUID, first: 25 },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    });

  const { lastPage, currentPage } = pagesInfo;

  const emptyDataPlaceholder = useMemo(() => {
    if (tab === 'payments') {
      return permissions?.has('payment_browse')
        ? t('payments.placeholder')
        : t('permission.error');
    }
    if (tab === 'bonus') return t('bonuses.placeholder');
    if (tab === 'transfers') return t('transfers.placeholder');
  }, [t, permissions, tab]);

  const loadPaymentsHandler = useCallback(
    (page: number) => {
      if (!permissions?.has('payment_browse')) return;

      loadPayments({
        variables: {
          uuid: clientUUID,
          first: 25,
          page,
        },
      }).then((res) => {
        if (res.data?.getPaymentsByClientId.paginatorInfo) {
          setPagesInfo({ ...res.data?.getPaymentsByClientId.paginatorInfo });
        }
      });
    },
    [loadPayments]
  );

  const loadBonusHandler = useCallback(
    (page: number) => {
      loadBonus({
        variables: {
          uuid: clientUUID,
          first: 25,
          page,
        },
      }).then((res) => {
        if (res.data?.getBonusHistory.paginatorInfo) {
          setPagesInfo({ ...res.data?.getBonusHistory.paginatorInfo });
        }
      });
    },
    [loadBonus]
  );

  const loadTransfersHandler = useCallback(
    (page: number) => {
      loadTransfers({
        variables: {
          uuid: clientUUID,
          first: 25,
          page,
        },
      }).then((res) => {
        if (res.data?.getClientDepositTransfers.paginatorInfo) {
          setPagesInfo({
            ...res.data?.getClientDepositTransfers.paginatorInfo,
          });
        }
      });
    },
    [loadTransfers]
  );

  useEffect(() => {
    loadPaymentsHandler(1);
  }, []);

  const changeTab = useCallback(
    (value: 'payments' | 'bonus' | 'transfers') => {
      if (value === 'payments') {
        loadPaymentsHandler(1);
      } else if (value === 'bonus') {
        loadBonusHandler(1);
      } else if (value === 'transfers') {
        loadTransfersHandler(1);
      }

      setTabs(value);
    },
    [tab, loadBonusHandler, loadPaymentsHandler, loadTransfersHandler]
  );

  return (
    <div className={s.container}>
      {tab === 'payments' && (
        <Table
          columns={getColumnsPayments(t, currency)}
          data={payments?.getPaymentsByClientId?.data ?? []}
          headerBlock={
            <Tabs
              options={TABS}
              onChange={changeTab}
              value={tab}
              className={s.tabs}
            />
          }
          isLoading={loading || loadingBonus}
          emptyDataPlaceholder={emptyDataPlaceholder}
          className={s['payment-history-table']}
          manualSortBy
        />
      )}
      {tab === 'bonus' && (
        <Table
          columns={getColumnsBonus(t)}
          data={bonus?.getBonusHistory?.data ?? []}
          headerBlock={
            <Tabs
              options={TABS}
              onChange={changeTab}
              value={tab}
              className={s.tabs}
            />
          }
          isLoading={loading || loadingBonus}
          emptyDataPlaceholder={emptyDataPlaceholder}
          className={s['payment-history-table']}
        />
      )}

      {tab === 'transfers' && (
        <Table
          columns={getColumnsTransfers(t, currency)}
          data={transfer?.getClientDepositTransfers?.data ?? []}
          headerBlock={
            <Tabs
              options={TABS}
              onChange={changeTab}
              value={tab}
              className={s.tabs}
            />
          }
          isLoading={loading || loadingTransfers}
          emptyDataPlaceholder={emptyDataPlaceholder}
          className={s['payment-history-table']}
        />
      )}

      <Pagination
        onChange={(page) => {
          setPagesInfo((state) => ({ ...state, currentPage: page }));

          if (tab === 'payments') {
            loadPaymentsHandler(page);
          } else if (tab === 'bonus') {
            loadBonusHandler(page);
          } else if (tab === 'transfers') {
            loadTransfersHandler(page);
          }
        }}
        lastPage={lastPage}
        currentPage={currentPage}
      />
    </div>
  );
};
